import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ProductCard from '../ProductCard';
import Button from '../Button';
import { usePageContent } from '../../hooks/usePageContent';
import styles from './index.module.css';

const getCategoryKey = (p) => p.ProductName;

function useInitialTileCount() {
  const getCount = () => {
    if (window.matchMedia('(min-width: 1441px)').matches) return 5;
    if (window.matchMedia('(min-width: 1080px)').matches) return 4;
    if (window.matchMedia('(min-width: 769px)').matches) return 3;
    return 4;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const mqs = [
      window.matchMedia('(min-width: 1441px)'),
      window.matchMedia('(min-width: 1080px)'),
      window.matchMedia('(min-width: 769px)'),
    ];
    const update = () => setCount(getCount());
    mqs.forEach(mq => mq.addEventListener('change', update));
    return () => mqs.forEach(mq => mq.removeEventListener('change', update));
  }, []);
  return count;
}

const ProductList = ({
  products = [],
  selectedProduct = null,
  onProductClick,
  getProductHref = null,
  displayCount,
  onLoadMore,
  title = 'Available styles',
  loading = false,
  formatPrice,
  unavailable = false,
  totalCount,
  productImages = {},
}) => {
  const { find } = usePageContent('product-list');
  const c = find('productList') || {};

  const initialTileCount = useInitialTileCount();
  const [expandedCategories, setExpandedCategories] = useState({});

  const displayedProducts = displayCount ? products.slice(0, displayCount) : products;
  const hasMore = displayCount && displayCount < products.length;

  const hasCategoryData = displayedProducts.some(
    (p) => p._variantColour != null || p._originalProductId != null
  );

  const renderCard = (product) => (
    <ProductCard
      key={product.ProductId}
      product={product}
      selected={selectedProduct?.ProductId === product.ProductId}
      onClick={getProductHref ? undefined : onProductClick}
      href={getProductHref ? getProductHref(product) : undefined}
      unavailable={unavailable}
      formatPrice={formatPrice}
      generatedImage={productImages[product.ProductId]}
    />
  );

  const emptyAlert = (
    <span className="products__title-option" role="alert">
      {c.noStylesText || 'No styles are available. Adjust your filter configuration to see more options.'}
    </span>
  );

  const paginationText = (
    <p className="products__pagination" role="region" aria-live="polite" aria-atomic="true">
      {c.showingPrefix || 'Showing'} <span className="products__pagination-amount">{totalCount}</span> {c.showingSuffix || 'plate ranges'}
    </p>
  );

  if (hasCategoryData) {
    const categoryOrder = [];
    const grouped = {};
    for (const p of displayedProducts) {
      const cat = getCategoryKey(p);
      if (!grouped[cat]) {
        grouped[cat] = [];
        categoryOrder.push(cat);
      }
      grouped[cat].push(p);
    }

    const toggleCategory = (cat) =>
      setExpandedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));

    return (
      <div
        className={`products__section ${
          unavailable ? 'products-section--unavailable' : 'products-section--available'
        }`}
      >
        <div className="products__header">
          <h3 className="products__title" tabIndex="-1">
            {loading ? (
              <span className="products__title-option" />
            ) : displayedProducts.length === 0 ? (
              emptyAlert
            ) : (
              <span className="products__title-option">{title}</span>
            )}
          </h3>
          {paginationText}
        </div>

        {categoryOrder.map((cat) => {
          const cards = grouped[cat];
          const isExpanded = !!expandedCategories[cat];
          const visibleCards = isExpanded ? cards : cards.slice(0, initialTileCount);
          const hasHidden = cards.length > initialTileCount;

          return (
            <div key={cat} className={`products__category-group ${styles.categoryGroup}`}>
              <h4 className={`products__category-title products__title ${styles.categoryTitle}`}>{cat.toUpperCase()}</h4>
              <ul className={`products__list${isExpanded ? ' products__list--expanded' : ''}`}>
                {visibleCards.map(renderCard)}
              </ul>
              {hasHidden && (
                <div className="products__actions">
                  <Button variant="" className='styles__range-more' onClick={() => toggleCategory(cat)}>
                    {isExpanded
                      ? (c.showLessText || 'Show less')
                      : (c.viewAllText || 'View all')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`products__section ${
        unavailable ? 'products-section--unavailable' : 'products-section--available'
      }`}
    >
      <div className="products__header">
        <h3 className="products__title" tabIndex="-1">
          {loading ? (
            <span className="products__title-option" />
          ) : displayedProducts.length === 0 ? (
            emptyAlert
          ) : (
            <span className="products__title-option">{title}</span>
          )}
        </h3>
        {paginationText}
      </div>

      <ul className="products__list">
        {displayedProducts.map(renderCard)}
      </ul>

      {hasMore && (
        <div className="products__actions">
          <Button variant="secondary" fullWidth onClick={onLoadMore}>
            {c.loadMoreText || 'Load more styles'}
          </Button>
        </div>
      )}
    </div>
  );
};

ProductList.propTypes = {
  products: PropTypes.array,
  selectedProduct: PropTypes.object,
  onProductClick: PropTypes.func,
  getProductHref: PropTypes.func,
  displayCount: PropTypes.number,
  onLoadMore: PropTypes.func,
  title: PropTypes.string,
  loading: PropTypes.bool,
  formatPrice: PropTypes.func,
  unavailable: PropTypes.bool,
  productImages: PropTypes.object,
  totalCount: PropTypes.number
};

export default ProductList;
